import { Box, Collapse, Grid, useMediaQuery, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import { CustomerHeader } from 'src/components/sidemenu/CustomerHeader'
import { CenteredSpinner } from 'src/components/Spinner'
import { ICustomerNameWithPermission } from 'src/generated-types'
import { Padding } from 'src/styles/paddingComponent'
import NoLocalities from '../../../assets/svg/no-locality-content.svg'
import { useNavigate } from 'react-router-dom'
import { AppRoutesNavigation } from 'src/app-routes'
import ProjectHeader from 'src/components/sidemenu/ProjectHeader'
import { IProjectInfo } from 'src/generated-types/project'
import { useGetAuthorizedProjects } from 'src/api/project'
import { ProjectFilter, ProjectFilterOption } from 'src/generated-types/project-filter'
import { StyledFilteredItemChip } from 'src/screens/localities/maintenance/EquipmentMaintenanceContainer'
import { IProjectStatus } from 'src/generated-types/project-status'
import ProjectsTable from '../ProjectTable'

const AllProjects: React.FC<{
  customers: ICustomerNameWithPermission[] | undefined
  filterOptions: ProjectFilter
  setFilterOptions: React.Dispatch<React.SetStateAction<ProjectFilter>>
  setMenuItems: React.Dispatch<React.SetStateAction<ProjectFilter>>
  cardView: boolean
  searchInput: string
}> = ({ customers, filterOptions, setFilterOptions, setMenuItems, cardView, searchInput }) => {
  const { t } = useTranslation('projects')
  const [customerHasProjects, setCustomerHasProjects] = useState<Record<number, boolean | undefined>>({})

  const handleCustomerProjectsLoaded = useCallback((index: number, hasProjects: boolean | undefined) => {
    setCustomerHasProjects(previous => {
      if (previous[index] === hasProjects) return previous
      return { ...previous, [index]: hasProjects }
    })
  }, [])

  const areAllCustomersLoaded = Boolean(
    customers?.length && customers.every((_, index) => customerHasProjects[index] !== undefined),
  )
  const firstVisibleCustomerIndex = areAllCustomersLoaded
    ? customers?.findIndex((_, index) => customerHasProjects[index]) ?? -1
    : 0

  const handleDelete = (category: keyof ProjectFilter, value: ProjectFilterOption) => {
    setFilterOptions(prev => {
      if (category === 'createdDate') return { ...prev, createdDate: { startDate: '', endDate: '' } }
      const categoryValues = prev[category]
      const updatedCategory = Array.isArray(categoryValues) ? categoryValues.filter(item => item !== value) : []
      return { ...prev, [category]: updatedCategory.length ? updatedCategory : [] }
    })
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', paddingY: 1 }}>
        {Object.entries(filterOptions).flatMap(([category, values]) => {
          // handle date range filter differently
          if (
            category === 'createdDate' &&
            values &&
            typeof values === 'object' &&
            'startDate' in values &&
            'endDate' in values
          ) {
            const dateRange = values as { startDate: string; endDate: string }
            if (dateRange.startDate || dateRange.endDate) {
              return [
                <StyledFilteredItemChip
                  key={`${category}`}
                  label={`${t('created-date')}: ${dateRange.startDate.split('T')[0]} - 
                            ${dateRange.endDate.split('T')[0]}`}
                  onDelete={() => handleDelete('createdDate', {} as ProjectFilterOption)}
                />,
              ]
            }
            return []
          }

          return Array.isArray(values) && values.length > 0
            ? values.map(value => (
                <StyledFilteredItemChip
                  key={`${category}-${value.id}`}
                  label={`${value.category || category} : ${value.category === 'Status' ? t(value.name) : value.name}`}
                  onDelete={() => handleDelete(category as keyof ProjectFilter, value)}
                />
              ))
            : []
        })}
      </Box>
      {Object.values(customerHasProjects).every(hasProjects => hasProjects === false) && (
        <Box py={8} className="text-center" height={'100%'}>
          <EmptyStatusCard
            imageWidth="132px"
            image={NoLocalities}
            header={t('projects-empty-status-title', { ns: 'projects' })}
            content={t('projects-empty-status-content', { ns: 'projects' })}
            headerFontSize="18px"
            contentFontSize="14px"
            contentTopMargin="10px"
            imageContainerClasses="pb-4"
            height="100%"
            contentClasses="mt-1"
          />
        </Box>
      )}
      {customers?.map((customer: ICustomerNameWithPermission, index: number) => {
        return (
          <CustomerProjectSection
            customer={customer}
            key={index}
            filterOptions={filterOptions}
            setMenuItems={setMenuItems}
            cardView={cardView}
            searchInput={searchInput}
            isCollapsedFirstCustomer={index === firstVisibleCustomerIndex}
            onProjectsLoaded={hasProjects => handleCustomerProjectsLoaded(index, hasProjects)}
          />
        )
      })}
    </Box>
  )
}
export default AllProjects

interface ICustomerProjectSectionProps {
  customer: ICustomerNameWithPermission
  filterOptions: ProjectFilter
  setMenuItems: React.Dispatch<React.SetStateAction<ProjectFilter>>
  cardView: boolean
  searchInput: string
  isCollapsedFirstCustomer?: boolean
  onProjectsLoaded: (hasProjects: boolean | undefined) => void
}

const CustomerProjectSection: React.FC<ICustomerProjectSectionProps> = ({
  customer,
  filterOptions,
  setMenuItems,
  cardView,
  searchInput,
  isCollapsedFirstCustomer = false,
  onProjectsLoaded,
}) => {
  const { data: projects, isLoading } = useGetAuthorizedProjects(
    customer.id,
    searchInput,
    false,
    filterOptions.localityIds?.map(l => l.id),
    filterOptions.responsibleUserIds?.map(u => u.id),
    filterOptions.createdDate?.startDate,
    filterOptions.createdDate?.endDate,
    filterOptions.status?.map(s => IProjectStatus[s.id]),
    filterOptions.ourReferences?.map(r => r.id),
  )

  const [open, setOpen] = React.useState<boolean>(false)
  const sortedProjects = useMemo(() => {
    if (projects && projects.length > 0) {
      return [...projects].sort((a, b) => {
        if (a.status !== b.status) {
          return a.status - b.status
        }
        return a.name.localeCompare(b.name)
      })
    }
  }, [projects])

  useEffect(() => {
    onProjectsLoaded(isLoading ? undefined : Boolean(sortedProjects?.length))
  }, [isLoading, onProjectsLoaded, sortedProjects])

  useEffect(() => {
    if (sortedProjects && sortedProjects.length > 0) {
      const responsibleUserMap = new Map()
      const localityMap = new Map()
      const ourReferenceMap = new Map()

      sortedProjects.forEach(project => {
        if (project.customerContact?.id) {
          responsibleUserMap.set(project.customerContact.id, {
            id: project.customerContact.id,
            name: project.customerContact.email,
          })
        }
        if (project.locality?.id) {
          localityMap.set(project.locality.id, {
            id: project.locality.id,
            name: project.locality.name,
          })
        }
        if (project.ourContact?.id) {
          ourReferenceMap.set(project.ourContact.id, {
            id: project.ourContact.id,
            name: project.ourContact.email,
          })
        }
      })

      setMenuItems(prevOptions => {
        const existingUsers = new Map((prevOptions.responsibleUserIds || []).map(user => [user.id, user]))
        const existingLocalities = new Map((prevOptions.localityIds || []).map(locality => [locality.id, locality]))
        const existingOurReferences = new Map((prevOptions.ourReferences || []).map(ref => [ref.id, ref]))

        // Merge with new values to get unique menuItems
        const mergedUsers = new Map([
          ...Array.from(existingUsers.entries()),
          ...Array.from(responsibleUserMap.entries()),
        ])
        const mergedLocalities = new Map([
          ...Array.from(existingLocalities.entries()),
          ...Array.from(localityMap.entries()),
        ])
        const mergedOurReferences = new Map([
          ...Array.from(existingOurReferences.entries()),
          ...Array.from(ourReferenceMap.entries()),
        ])

        return {
          ...prevOptions,
          responsibleUserIds: Array.from(mergedUsers.values()),
          localityIds: Array.from(mergedLocalities.values()),
          ourReferences: Array.from(mergedOurReferences.values()),
        }
      })
    }
  }, [sortedProjects, setMenuItems])

  const hasActiveFilters = useMemo(
    () =>
      filterOptions.createdDate?.startDate ||
      filterOptions.createdDate?.endDate ||
      filterOptions.localityIds?.length > 0 ||
      filterOptions.responsibleUserIds?.length > 0 ||
      filterOptions.status?.length > 0 ||
      filterOptions.ourReferences?.length > 0 ||
      searchInput.length > 0,
    [filterOptions, searchInput],
  )

  useEffect(() => {
    if (hasActiveFilters) {
      setOpen(Boolean(sortedProjects && sortedProjects.length > 0))
    } else {
      setOpen(isCollapsedFirstCustomer)
    }
  }, [sortedProjects, hasActiveFilters, isCollapsedFirstCustomer])

  if (isLoading || (!isLoading && !sortedProjects?.length)) return null

  return (
    <>
      <CustomerHeader
        hasAdminAccess={customer.isAdminCustomer}
        isLandingPage={true}
        isGlobalServiceStationProvider={false}
        image={customer.image ?? ''}
        {...customer}
        onClick={() => {
          localStorage.setItem('currentCustomerId', customer.id.toString())
          setOpen(prev => !prev)
        }}
        active={open}
        isProject={true}
      />
      <Padding.pb12>{open}</Padding.pb12>
      <Box className="overflow-y-auto">
        <Collapse in={open}>
          <CustomerProjectContentSection isLoading={isLoading} projects={sortedProjects || []} cardView={cardView} />
        </Collapse>
      </Box>
    </>
  )
}

const CustomerProjectContentSection: React.FC<{
  projects: IProjectInfo[]
  isLoading: boolean
  cardView: boolean
}> = ({ projects, isLoading, cardView }) => {
  const { t } = useTranslation('projects')
  const theme = useTheme()
  const isMobile = useMediaQuery<any>(theme.breakpoints.down('sm'))

  const navigate = useNavigate()
  if (isLoading) return <CenteredSpinner />

  return (
    <>
      {projects.length === 0 ? (
        <Box py={8} className="text-center" height={'100%'}>
          <EmptyStatusCard
            imageWidth="132px"
            image={NoLocalities}
            header={t('projects-empty-status-title', { ns: 'projects' })}
            content={t('projects-empty-status-content', { ns: 'projects' })}
            headerFontSize="18px"
            contentFontSize="14px"
            contentTopMargin="10px"
            imageContainerClasses="pb-4"
            height="100%"
            contentClasses="mt-1"
          />
        </Box>
      ) : (
        <Box sx={{ paddingTop: '12px', paddingBottom: '16px' }}>
          {cardView ? (
            <Grid container spacing={isMobile ? '16px' : '11px'} columns={isMobile ? 12 : 14}>
              {projects?.map((project, index) => (
                <Grid item md={2} sm={4} xs={6} key={index}>
                  <ProjectHeader
                    project={project}
                    customerId={project.customerId}
                    onClick={() =>
                      navigate(AppRoutesNavigation.Project.project(project.customerId, project.localityId, project.id))
                    }
                    status={project.status}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <ProjectsTable projects={projects} />
          )}
        </Box>
      )}
    </>
  )
}
